/**
 * ClinicalGestures — detecta gestos clínicos de procedimiento a partir del
 * feed de MediaPipe (HandState[] por frame). A diferencia de GestureRecognizer,
 * que maneja interacción con el grafo (pinch, grab, swipe), este módulo se
 * enfoca SOLO en procedimientos que el alumno hace con las manos frente a
 * la cámara:
 *
 *   - chest-compressions: dos manos apiladas, movimiento vertical rítmico
 *     100-120 compresiones/min (Leo: "eso es lo que se evalúa en OSCE")
 *   - defib-shock: dos manos extendidas, empuje frontal simultáneo
 *   - pulse-check: dos dedos (índice+medio) extendidos cerca del cuello
 *
 * El MonitorDisplay se suscribe a los eventos y aplica consecuencias clínicas
 * (logging, cambios en vitales, defibrilación si el ritmo es shockable).
 *
 * No mockea landmarks: si no hay manos, el módulo simplemente no dispara.
 * Los thresholds son conservadores para evitar disparar en gestos casuales.
 */

import type { HandState } from '../core/state';

export type ClinicalGestureId = 'chest-compressions' | 'defib-shock' | 'pulse-check';

/**
 * Sensibilidad del detector. Ajustable por el alumno desde el panel del
 * monitor. Más sensible = se dispara con menos esfuerzo (pero más falsos
 * positivos); más estricto = requiere gestos más marcados.
 */
export type GestureSensitivity = 'low' | 'medium' | 'high';

interface ThresholdSet {
  minCompressionAmplitude: number;
  stackedMaxXZ: number;
  defibThrustDz: number;
  pulseHoldMs: number;
  minCompressionsFire: number;
}

const SENSITIVITY_PRESETS: Record<GestureSensitivity, ThresholdSet> = {
  // "low" = más estricto (cuesta disparar). Ideal para entornos ruidosos
  // o usuarios expertos que quieren gestos amplios y claros.
  low: {
    minCompressionAmplitude: 0.35,
    stackedMaxXZ: 0.45,
    defibThrustDz: 0.5,
    pulseHoldMs: 2000,
    minCompressionsFire: 8,
  },
  medium: {
    minCompressionAmplitude: 0.25,
    stackedMaxXZ: 0.6,
    defibThrustDz: 0.35,
    pulseHoldMs: 1500,
    minCompressionsFire: 6,
  },
  // "high" = más sensible (basta poco esfuerzo). Útil cuando la cámara está
  // lejos o el alumno tiene poco rango de movimiento.
  high: {
    minCompressionAmplitude: 0.18,
    stackedMaxXZ: 0.8,
    defibThrustDz: 0.22,
    pulseHoldMs: 1000,
    minCompressionsFire: 4,
  },
};

export interface ClinicalGestureEvent {
  id: ClinicalGestureId;
  at: number;
  /** Datos contextuales del gesto (ej. rate para compresiones). */
  data?: Record<string, number | string>;
}

type Listener = (ev: ClinicalGestureEvent) => void;

/**
 * Detector principal. Pensado como singleton: `clinicalGestures.feed(hands)`
 * en cada frame, y `onGesture(cb)` para suscribirse.
 */
export class ClinicalGestures {
  private listeners: Listener[] = [];

  // ─── Chest compressions ──────────────────────────────────────────────
  /** Y de las manos en los últimos ~2s (para detectar oscilación rítmica). */
  private compressionHistory: { t: number; y: number }[] = [];
  /** Momento del último ciclo contado (mínimo local → máximo local). */
  private lastCompressionAt = 0;
  /** Compresiones contadas en la ráfaga actual. */
  private compressionCount = 0;
  /** Momento en que empezó la ráfaga actual. */
  private compressionBurstStart = 0;
  /** Último "fire" reportado, para no spammear listeners cada compresión. */
  private lastCompressionFire = 0;

  // ─── Defib shock ─────────────────────────────────────────────────────
  /** Última posición Z (media ambas manos) para detectar thrust. */
  private lastDefibZ: number | null = null;
  private lastDefibFire = 0;

  // ─── Pulse check ─────────────────────────────────────────────────────
  private pulseHoldStart = 0;
  private lastPulseFire = 0;

  // ─── Config ──────────────────────────────────────────────────────────
  /** Ventana para contar rate de compresiones. */
  private static readonly BURST_WINDOW_MS = 8000;
  /** Cooldown tras fire para no spamear. */
  private static readonly COMPRESSION_COOLDOWN_MS = 4000;
  /** Cooldown de defib. */
  private static readonly DEFIB_COOLDOWN_MS = 3000;
  /** Cooldown de pulse check. */
  private static readonly PULSE_COOLDOWN_MS = 5000;

  /** Thresholds ajustables vía sensibilidad (ver setSensitivity). */
  private thr: ThresholdSet = SENSITIVITY_PRESETS.medium;
  private sensitivity: GestureSensitivity = 'medium';

  setSensitivity(level: GestureSensitivity): void {
    this.sensitivity = level;
    this.thr = SENSITIVITY_PRESETS[level];
    try { localStorage.setItem('cgt_gesture_sensitivity', level); } catch { /* ignore */ }
  }

  getSensitivity(): GestureSensitivity { return this.sensitivity; }

  /** Carga la sensibilidad guardada (llamar al iniciar). */
  loadSensitivity(): void {
    try {
      const saved = localStorage.getItem('cgt_gesture_sensitivity') as GestureSensitivity | null;
      if (saved && SENSITIVITY_PRESETS[saved]) this.setSensitivity(saved);
    } catch { /* ignore */ }
  }

  onGesture(cb: Listener): () => void {
    this.listeners.push(cb);
    return () => { this.listeners = this.listeners.filter(l => l !== cb); };
  }

  /** Procesa un frame de detección. Llamar desde HandController.onHandsDetected. */
  feed(hands: HandState[]): void {
    const present = hands.filter(h => h.isPresent);
    const now = Date.now();

    // Limpieza de buffers cuando se pierden las manos
    if (present.length === 0) {
      this.compressionHistory = [];
      this.lastDefibZ = null;
      this.pulseHoldStart = 0;
      return;
    }

    if (present.length >= 2) {
      this.detectCompressions(present[0], present[1], now);
      this.detectDefib(present[0], present[1], now);
    } else {
      // Con una sola mano, limpiar estados que requieren dos
      this.compressionHistory = [];
      this.lastDefibZ = null;
    }

    this.detectPulseCheck(present, now);
  }

  // ───────────────────────────────────────────────────────────────────────
  // RCP: oscilación vertical rítmica con dos manos apiladas
  // ───────────────────────────────────────────────────────────────────────

  private detectCompressions(a: HandState, b: HandState, now: number): void {
    // Verificar que las manos estén "apiladas": XZ cercanos, separadas en Y.
    const dx = Math.abs(a.position.x - b.position.x);
    const dz = Math.abs(a.position.z - b.position.z);
    if (dx > this.thr.stackedMaxXZ || dz > this.thr.stackedMaxXZ) {
      return; // no están apiladas; podrían ser dos manos exploratorias
    }

    // Y promedio para tracking del ciclo
    const meanY = (a.position.y + b.position.y) / 2;
    this.compressionHistory.push({ t: now, y: meanY });

    // Podar ventana
    const cutoff = now - ClinicalGestures.BURST_WINDOW_MS;
    while (this.compressionHistory.length > 0 && this.compressionHistory[0].t < cutoff) {
      this.compressionHistory.shift();
    }
    if (this.compressionHistory.length < 10) return;

    // Detectar ciclo: un ciclo = min local seguido de max local con amplitud
    // suficiente. Simplificado: contar zero crossings respecto a la media.
    const ys = this.compressionHistory.map(p => p.y);
    const avg = ys.reduce((s, v) => s + v, 0) / ys.length;
    const max = Math.max(...ys);
    const min = Math.min(...ys);
    const amplitude = max - min;
    if (amplitude < this.thr.minCompressionAmplitude) return;

    // Detectar cruce descendente (indicador de compresión: baja el tórax).
    // Usamos el último punto vs. avg y el penúltimo vs. avg.
    const n = this.compressionHistory.length;
    const curr = this.compressionHistory[n - 1].y - avg;
    const prev = this.compressionHistory[n - 2].y - avg;
    const crossedDown = prev >= 0 && curr < 0;

    if (crossedDown && now - this.lastCompressionAt > 300) {
      // Al menos 300ms entre compresiones (equivale a <200/min; protege
      // contra temblor o ruido del filtro que podría dar picos falsos).
      if (this.compressionBurstStart === 0) this.compressionBurstStart = now;
      this.compressionCount++;
      this.lastCompressionAt = now;

      // Reportar cuando se acumulen suficientes compresiones Y haya pasado
      // el cooldown, con el rate instantáneo.
      const sinceBurst = now - this.compressionBurstStart;
      const sinceLastFire = now - this.lastCompressionFire;

      if (this.compressionCount >= this.thr.minCompressionsFire
          && sinceLastFire > ClinicalGestures.COMPRESSION_COOLDOWN_MS
          && sinceBurst > 2000) {
        const ratePerMin = Math.round((this.compressionCount / sinceBurst) * 60_000);
        this.emit({
          id: 'chest-compressions',
          at: now,
          data: { count: this.compressionCount, rate: ratePerMin },
        });
        this.lastCompressionFire = now;
        // Resetear burst para contar la siguiente ráfaga
        this.compressionCount = 0;
        this.compressionBurstStart = 0;
      }
    }

    // Si no hay compresiones en 3s, resetear el burst
    if (now - this.lastCompressionAt > 3000) {
      this.compressionCount = 0;
      this.compressionBurstStart = 0;
    }
  }

  // ───────────────────────────────────────────────────────────────────────
  // Desfibrilación: empuje frontal simultáneo de dos manos abiertas
  // ───────────────────────────────────────────────────────────────────────

  private detectDefib(a: HandState, b: HandState, now: number): void {
    if (now - this.lastDefibFire < ClinicalGestures.DEFIB_COOLDOWN_MS) return;

    // Manos separadas lateralmente (no apiladas) — distintivo de palas
    const dx = Math.abs(a.position.x - b.position.x);
    if (dx < 0.4) return;

    // Ambas muy abiertas (palmas)
    const openA = this.openness(a);
    const openB = this.openness(b);
    if (openA < 0.85 || openB < 0.85) {
      this.lastDefibZ = null;
      return;
    }

    const meanZ = (a.position.z + b.position.z) / 2;
    if (this.lastDefibZ === null) {
      this.lastDefibZ = meanZ;
      return;
    }
    const dz = this.lastDefibZ - meanZ; // positivo = hacia cámara
    this.lastDefibZ = meanZ;

    if (dz > this.thr.defibThrustDz) {
      this.emit({
        id: 'defib-shock',
        at: now,
        data: { thrust: Number(dz.toFixed(2)) },
      });
      this.lastDefibFire = now;
    }
  }

  // ───────────────────────────────────────────────────────────────────────
  // Pulse check: dos dedos extendidos (índice+medio) sostenidos 1.5s
  // ───────────────────────────────────────────────────────────────────────

  private detectPulseCheck(hands: HandState[], now: number): void {
    if (now - this.lastPulseFire < ClinicalGestures.PULSE_COOLDOWN_MS) return;

    // Buscar una mano con postura de "V": índice y medio extendidos,
    // anular y meñique flexionados, pulgar pegado. Usamos landmarks raw.
    const hand = hands.find(h => h.landmarks && this.isPulseCheckPose(h.landmarks));
    if (!hand) {
      this.pulseHoldStart = 0;
      return;
    }

    if (this.pulseHoldStart === 0) {
      this.pulseHoldStart = now;
    } else if (now - this.pulseHoldStart >= this.thr.pulseHoldMs) {
      this.emit({ id: 'pulse-check', at: now });
      this.lastPulseFire = now;
      this.pulseHoldStart = 0;
    }
  }

  /**
   * Heurística: un dedo está "extendido" si su tip está significativamente
   * más lejos de la muñeca que su MCP (base), y "flexionado" si están cerca.
   */
  private isPulseCheckPose(lm: Array<{x: number; y: number; z: number}>): boolean {
    const wrist = lm[0];
    const dist = (i: number): number => {
      const p = lm[i];
      const dx = p.x - wrist.x;
      const dy = p.y - wrist.y;
      return Math.sqrt(dx * dx + dy * dy);
    };

    // Extendidos: índice(8) y medio(12) lejos de muñeca (>0.25)
    // Flexionados: anular(16) y meñique(20) cerca (<0.2)
    const indexOut = dist(8) > 0.25;
    const middleOut = dist(12) > 0.25;
    const ringIn = dist(16) < 0.2;
    const pinkyIn = dist(20) < 0.2;
    return indexOut && middleOut && ringIn && pinkyIn;
  }

  // ───────────────────────────────────────────────────────────────────────

  private openness(h: HandState): number {
    if (!h.landmarks) return 0;
    const wrist = h.landmarks[0];
    const mcp = [5, 9, 13, 17];
    let spread = 0;
    mcp.forEach(i => {
      const j = h.landmarks![i];
      const dx = j.x - wrist.x;
      const dy = j.y - wrist.y;
      spread += Math.sqrt(dx * dx + dy * dy);
    });
    return Math.min(1, (spread / mcp.length) * 3);
  }

  private emit(ev: ClinicalGestureEvent): void {
    this.listeners.forEach(l => {
      try { l(ev); } catch (e) { console.warn('[ClinicalGestures] listener error:', e); }
    });
  }

  /**
   * Estado en vivo del detector para feedback visual al alumno. Devuelve
   * 0..1 por gesto: cuánto falta para que dispare. Se llama en cada frame
   * desde el HUD del monitor para mostrar barras de progreso.
   */
  getDetectionState(): { compressions: number; defib: number; pulse: number; compressionRate: number } {
    const now = Date.now();
    // RCP: progreso = ratio de compresiones acumuladas vs el mínimo requerido
    const burstActive = this.compressionBurstStart > 0 && (now - this.lastCompressionAt) < 3000;
    const compProgress = burstActive
      ? Math.min(1, this.compressionCount / this.thr.minCompressionsFire)
      : 0;
    // Rate instantáneo (0 si no hay burst activo)
    const sinceBurst = burstActive ? now - this.compressionBurstStart : 0;
    const rate = sinceBurst > 1000 && this.compressionCount > 0
      ? Math.round((this.compressionCount / sinceBurst) * 60_000)
      : 0;

    // Defib: si tenemos lastDefibZ no-null, las palmas están listas; el dz
    // es el progreso del thrust en curso. Aproximamos con un boolean lite.
    const defibProgress = this.lastDefibZ !== null && !this.lastDefibFire ? 0.5 : 0;

    // Pulse: ratio de hold vs hold requerido
    const pulseProgress = this.pulseHoldStart > 0
      ? Math.min(1, (now - this.pulseHoldStart) / this.thr.pulseHoldMs)
      : 0;

    return { compressions: compProgress, defib: defibProgress, pulse: pulseProgress, compressionRate: rate };
  }

  reset(): void {
    this.compressionHistory = [];
    this.lastCompressionAt = 0;
    this.compressionCount = 0;
    this.compressionBurstStart = 0;
    this.lastCompressionFire = 0;
    this.lastDefibZ = null;
    this.lastDefibFire = 0;
    this.pulseHoldStart = 0;
    this.lastPulseFire = 0;
  }
}

export const clinicalGestures = new ClinicalGestures();
